import * as AccrualDocumentsActions from './accrual-documents.actions';

import {createEntityAdapter, EntityAdapter} from '@ngrx/entity';
import {createReducer, on} from '@ngrx/store';
import dayjs from 'dayjs';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {AccrualDocument, AccrualDocumentsSearchParams} from '@accounting/_common/models/accrual-document.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const accrualDocumentsFeatureKey = 'accrualDocuments';

export function sortAccrualDocument(a: AccrualDocument, b: AccrualDocument) {
    if (a.date !== b.date) {
        return StoredComponentHelperService.sortArrayComparer(a, b, 'date');
    }

    if (a.number !== b.number) {
        return (b.number || '').localeCompare(a.number || '');
    }

    if (a.period !== b.period) {
        return (b.period || '').localeCompare(a.period || '');
    }

    return (a.id || '').localeCompare(b.id || '');
}

export const adapter: EntityAdapter<AccrualDocument> = createEntityAdapter<AccrualDocument>({
    sortComparer: sortAccrualDocument
});

export type AccrualDocumentsState = EntityStateModel<AccrualDocument, AccrualDocumentsSearchParams>;

export const initialState: AccrualDocumentsState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export const accrualDocumentsReducer = createReducer(
    initialState,

    on(AccrualDocumentsActions.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return adapter.removeAll({
                ...state,
                savedFilter: !action.payload.close && state.filter ? state.filter : undefined,
                pagination: initialState.pagination,
                filter: action.payload.close ? state.savedFilter : undefined
            });
        } else {
            if (action.payload.close) {
                return adapter.setAll(action.payload.items, {
                    ...state,
                    pagination: {...state.pagination, number: 0},
                    componentOpened: false
                });
            }

            return {...state, componentOpened: true};
        }
    }),

    on(AccrualDocumentsActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(AccrualDocumentsActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined

            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(AccrualDocumentsActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(AccrualDocumentsActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(AccrualDocumentsActions.SetPagination, (state, action) => ({
        ...state,
        pagination: {...state.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.pagination, state.ids?.length) : 0}
    })),

    on(AccrualDocumentsActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(AccrualDocumentsActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(AccrualDocumentsActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(action.payload!, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(AccrualDocumentsActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(AccrualDocumentsActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(AccrualDocumentsActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(AccrualDocumentsActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();

const isAvailableByFilter = (searchParams: AccrualDocumentsSearchParams | undefined, document: AccrualDocument): boolean => {
    if (!searchParams) {
        return true;
    }

    if ((searchParams.operative || searchParams.operative === false) && document.operative !== searchParams.operative) {
        return false;
    }

    if (searchParams.from && dayjs(searchParams.from).isAfter(dayjs(document.date))) {
        return false;
    }

    if (searchParams.to && dayjs(document.date).isAfter(dayjs(searchParams.to))) {
        return false;
    }

    if (searchParams.period && searchParams.period != document.period) {
        return false;
    }

    if (searchParams.accrualType && searchParams.accrualType != document.accrual) {
        return false;
    }

    return !(!document.comment && searchParams.query || searchParams.query && (document.comment as string).toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1);
};
